import { FormControl, FormControlLabel, FormControlProps, FormGroup, FormHelperText, FormLabel, Grid, Theme, useTheme, ThemeProvider, createTheme } from '@mui/material'
import { Field } from 'formik'
import React from 'react'
import { hasFieldValue, valueExistsTS } from 'utilsTS'
import { ErrorLabel } from '../forms/ErrorLabel'
import Checkbox from 'app/views/common-components/CustomCheckbox'

export const getMuiTheme = (existingTheme: Theme) => createTheme({
  ...existingTheme,
  components: {
    ...existingTheme?.components,
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: existingTheme.palette.primary.main,
          fontWeight: 'bold',
          marginBottom: 16,
          '&.Mui-error': {
            color: existingTheme.palette.primary.main
          }
        }
      }
    },
  }
})

export const CheckboxGroupWithFormik = (
  props: FormControlProps & CheckboxGroupWithFormikProps
): JSX.Element => {
  const { name, options, label, compact, helperText, renderInTwoColumns, row, onChange } = props

  const theme = useTheme()
  const _theme = getMuiTheme(theme)

  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const { setFieldValue, setTouched } = form
        const value = field.value ?? []
        const displayError = Boolean(
          (meta.touched || hasFieldValue(value)) as boolean && meta.error)

        return (
          <FormControl
            color={props.color}
            disabled={props.disabled}
            fullWidth={props.fullWidth}
            focused={props.focused}
            hiddenLabel={props.hiddenLabel}
            margin={props.margin}
            error={displayError}
            required={props.required}
            size={props.size}
            variant={props.variant}
            onChange={undefined}
            sx={{ ...props.sx }}
          >
            <ThemeProvider theme={getMuiTheme(_theme)}>
              <FormLabel>
                {label}
              </FormLabel>
            </ThemeProvider>
            <FormGroup
              row={row}
              style={valueExistsTS(renderInTwoColumns)
                ? {
                    flexDirection: 'row',
                    marginLeft: 25,
                    justifyContent: 'space-between'
                  }
                : {}}
            >
              {options.map(option => (
                <FormControlLabel
                  style={{
                    width: valueExistsTS(renderInTwoColumns) ? '45%' : undefined
                  }}
                  value={option.value}
                  key={option.value}
                  control={(
                    <Checkbox
                      style={valueExistsTS(compact) ? { padding: 3 } : {}}
                      checked={value.includes(option.value)}
                    />
                  )}
                  label={option.label}
                  onChange={(event, checked) => {
                    const newValue = [...value]
                    if (!newValue.includes(option.value)) {
                      newValue.push(option.value)
                    } else {
                      newValue.splice(newValue.indexOf(option.value), 1)
                    }
                    if (onChange != null) {
                      onChange(newValue)
                    } else {
                      setFieldValue(name, newValue)
                      if (meta.touched === false) {
                        setTouched(name)
                      }
                    }
                  }}
                />
              ))}
            </FormGroup>
            {Boolean(displayError) && (
              <Grid item xs>
                <ErrorLabel label={meta.error} />
              </Grid>
            )}
            <FormHelperText>{helperText}</FormHelperText>
          </FormControl>
        )
      }}
    </Field>
  )
}

interface OptionProps {
  label: React.ReactNode
  value: string
}

interface CheckboxGroupWithFormikProps {
  /**
   * This is an id of Formiks field that the TextField is connected to
   */
  name: string
  /**
   * List of options to be rendered in the group
   */
  options: OptionProps[]
  /**
   * Form control label to render on the top of checkbox group.
   */
  label?: React.ReactNode
  /**
   * If true, lower padding will be used for chekboxes instead of default one
   */
  compact?: boolean
  /**
   * If true, checkboxes will be rendered in two columns
   */
  renderInTwoColumns?: boolean
  /**
   * Helper text to render below the checkbox group.
   */
  helperText?: React.ReactNode
  /**
   * If true, checkboxes will be rendered in a row
   */
  row?: boolean
  onChange?: (value: any[]) => void
}
